import { AxiosError, AxiosResponse } from 'axios'
import { updateAppAlert } from '../../../common/modules/appAlert/actions'
import {
  startLoading,
  stopLoading,
} from '../../../common/modules/loading/actions'
import { LoadingTypes } from '../../../common/modules/loading/reducer'
import { getErrorMessages } from '../../../common/utils/error'
import { AppDispatch, RootState } from '../../../store'
import { errorLogger } from '../../../common/utils/errorLogger'
import { azureMockFieldMappings } from '../../data/referral/azure/azureFieldMappings'
import {
  postReferralTemplate,
  postReferralTemplateFromMappingService,
  deleteSingleReferralTemplate,
  deleteReferralTemplateFromMappingService,
  getAllReferralTemplates,
  getAllReferralTemplatesCrm,
  getHubspotFieldsForTemplate,
  getReferralTemplate,
  getReferralTemplatesFromMappingService,
  putReferralTemplate,
  putReferralTemplateFromMappingService,
  putCrmSectionData,
  postCloneReferralTemplate,
  postCloneReferralTemplateFromMappingService,
  resetReferralTemplate,
  getReferralMapping,
  getReferralMappingFromMappingService,
  patchReferralMappingFromMappingService,
  postReferralTemplateSalesforce,
  deleteReferralTemplateCrm as deleteReferralTemplateCrmAPI,
  getSyncConfigurationsCrm,
  postSalesforceSectionData,
  getReferralTemplateForSalesforce,
  getCrmFieldsForTemplate,
  putReferralTemplateForSalesforce,
  getLabraFields,
  postCrmFieldValues,
  putReferralMapping,
  patchHubspotSectionData,
} from '../../../common/api/mappingManager'
import { camelize, snakeize } from 'casing'
import {
  CoSellAwsPartnerUserDetails,
  CoSellCompanyAndContact,
  CoSellHubspotDealPipeline,
  CosellHubspotFieldsData,
  CoSellMappingManagerSectionName,
  CosellMappingManagerSections,
  CoSellSyncReferralRecord,
  CosellTemplateDataResponse,
  CosellTemplateDataResponseCRM,
  AutoCreateTriggerCriteria,
  CosellTemplateItem,
  CosellTemplatesDataFieldMappings,
  CosellTemplatesDetailsData,
  CosellTemplateSectionData,
  CoSellUpdateReferralRecord,
  AwsPartnerUserDetails,
  CosellFieldMapping,
  CosellReferralDataFieldMappings,
  CosellReferralMappingSectionData,
  CosellSyncNewReferralMapping,
  CosellSyncReferralMapping,
  CosellSyncReferralMappingEntry,
  CosellSyncReferralMappingPatch,
  MappingManagerTemplateStatusType,
  CosellMappingManagerSectionsForSalesforce,
  CoSellUpdateReferralRecordSalesforce,
  initialStateForSalesforce,
  CosellSalesforceFieldsData,
  CosellHubspotFieldsOptions,
  CoSellReferralMapping,
} from './reducer'
import { isEmpty } from 'lodash'
import {
  ReferralTemplateCreateFailureMessage,
  ReferralTemplateResetSuccess,
  ReferralTemplateSaveSuccess,
  RequestFailureMessage,
} from '../../../common/utils/messagesContants'
import { getCRMType } from '../../../common/api'
import { UserState } from '../userList/reducer'
import {
  getTransformedDataForSalesforceApi,
  getTransformedDataFromSalesforceApi,
  isCosellSalesforceMappingSectionData,
  isSalesforceTemplateIdInRequiredFieldsSlots,
  removeSalesforceTemplateIdFromStageMappingRefs,
} from './mappingMangerUtils'
import {
  setReferralDetails,
  referralAPIToForm,
  setReferralCreationData,
} from '../CoSell/ReferralForm/actions'
import {
  setReferralFormFields,
  setSolutionOfferedOptions,
  ReferralFormField,
} from '../CoSell/actions'
import { actionTypeWrapper } from '../../../common/utils/actionTypeWrapper'
import { CRMTypes } from '../../../common/utils/constants'
import { ProductType } from '../../../common/modules/partner/reducer'
import { SelectorOptions } from '@labrav/react-components/lib/@types/types/forms'

import { CosellMappingManagerActionTypes } from './coSellMappingManagerActionTypes'

export { CosellMappingManagerActionTypes }

export const setReferralTemplate = (
  referralTemplateData: CosellTemplateDataResponse
) => ({
  type: CosellMappingManagerActionTypes.SET_TEMPLATE_DATA,
  payload: referralTemplateData,
})

export const cacheSalesforceReferralTemplateForStageMapping = (payload: {
  templateId: string
  template: CosellTemplateDataResponse
}) => ({
  type: CosellMappingManagerActionTypes.CACHE_SALESFORCE_REFERRAL_TEMPLATE_FOR_STAGE_MAPPING,
  payload,
})

/** Prefetch for stage-mapping preview; no-op if already cached. Swallows errors (the preview modal still loads with user-visible errors). */
export const prefetchSalesforceReferralTemplateForStageMapping =
  (crmId: string, accountId: string, templateId: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const trimmedId = String(templateId).trim()
    if (!trimmedId || !String(crmId).trim() || !String(accountId).trim()) {
      return
    }
    if (
      getState().coSellMappingManager.salesforceReferralTemplateById[trimmedId]
    ) {
      return
    }
    try {
      const response = await getReferralTemplateForSalesforce(
        crmId,
        accountId,
        trimmedId
      )
      const data = camelize(response?.data) as CosellTemplateDataResponse
      dispatch(
        cacheSalesforceReferralTemplateForStageMapping({
          templateId: trimmedId,
          template: data,
        })
      )
    } catch {
      /* background prefetch */
    }
  }

export const setReferralMapping = (
  referralMappingData: CosellReferralDataFieldMappings
) => ({
  type: CosellMappingManagerActionTypes.SET_REFERRAL_MAPPING_DATA,
  payload: referralMappingData,
})

export const setReferralTemplatesInfo = (
  referralTemplateDataInfo: CosellTemplatesDetailsData[]
) => ({
  type: CosellMappingManagerActionTypes.SET_TEMPLATE_DATA_INFO,
  payload: referralTemplateDataInfo,
})

export const setAllHubspotFields = (fieldsData: CosellHubspotFieldsData[]) => ({
  type: CosellMappingManagerActionTypes.SET_HUBSPOT_FIELDS,
  payload: fieldsData,
})

export const setLabraFields = (fieldsData: CosellHubspotFieldsData[]) => ({
  type: CosellMappingManagerActionTypes.SET_LABRA_FIELDS,
  payload: fieldsData,
})
export const setAllSalesforceFields = (
  fieldsData: CosellSalesforceFieldsData
) => ({
  type: CosellMappingManagerActionTypes.SET_SALESFORCE_FIELDS,
  payload: fieldsData,
})

export const setSectionFields = (sectionData: CosellTemplateSectionData) => ({
  type: CosellMappingManagerActionTypes.SET_SECTION_WISE_FIELDS,
  payload: sectionData,
})

export const setSectionReferralMapping = (
  sectionData: CosellReferralMappingSectionData
) => ({
  type: CosellMappingManagerActionTypes.SET_SECTION_WISE_RFERRAL_MAPPING,
  payload: sectionData,
})

export const setCosellMappingSection = (
  sectionData:
    | CosellMappingManagerSections
    | CosellMappingManagerSectionsForSalesforce
) => ({
  type: CosellMappingManagerActionTypes.SET_CO_SELL_MAPPING_SECTION_DATA,
  payload: sectionData,
})

export const setCrmSchemaData = (crmSchemaData: any) => ({
  type: CosellMappingManagerActionTypes.SET_ORIGINAL_CRM_SCHEMA,
  payload: crmSchemaData,
})

export const setSyncConfigurations = (syncConfigurations: any) => ({
  type: CosellMappingManagerActionTypes.SET_SYNC_CONFIGURATIONS,
  payload: syncConfigurations,
})

export const updateTemplateSectionField = (
  section: keyof CosellTemplateSectionData,
  fieldKey: keyof CosellTemplatesDataFieldMappings,
  updates: Partial<CosellFieldMapping>
) => ({
  type: CosellMappingManagerActionTypes.UPDATE_TEMPLATE_SECTION_FIELD,
  payload: { section, fieldKey, updates },
})

export const updateMappingSectionField = (
  section: keyof CosellReferralMappingSectionData,
  fieldKey: keyof CosellReferralDataFieldMappings,
  updates: CosellSyncReferralMappingPatch
) => ({
  type: CosellMappingManagerActionTypes.UPDATE_MAPPING_SECTION_FILED,
  payload: { section, fieldKey, updates },
})

export const setTemplateAsDraft = (isDraft: boolean) => ({
  type: CosellMappingManagerActionTypes.SET_TEMPLATE_AS_DRAFT,
  payload: isDraft,
})

export const setAutomateReferralCreation = (autoReferralCreation: boolean) => ({
  type: CosellMappingManagerActionTypes.SET_AUTOMATE_REFERRAL_CREATION,
  payload: autoReferralCreation,
})

export const setAutoCreateTriggerCriteria = (
  criteria: AutoCreateTriggerCriteria | null
) => ({
  type: CosellMappingManagerActionTypes.SET_AUTO_CREATE_TRIGGER_CRITERIA,
  payload: criteria,
})

export const distributeFieldsByCategory = (
  fieldsData: CosellTemplatesDataFieldMappings
): CosellTemplateSectionData => {
  const sectionData: CosellTemplateSectionData = {
    customerDetails: {},
    referralDetails: {},
    awsDetails: {},
    contacts: {},
    dealDetails: {},
    solutionsOffered: {},
    helpFromMicrosoft: {},
  }

  Object.entries(fieldsData).forEach(([key, fieldMapping]) => {
    switch (fieldMapping.category) {
      case 'Customer details':
        sectionData.customerDetails[
          key as keyof CosellTemplatesDataFieldMappings
        ] = fieldMapping
        break
      case 'Referral details':
        sectionData.referralDetails[
          key as keyof CosellTemplatesDataFieldMappings
        ] = fieldMapping
        break
      case 'AWS details':
        sectionData.awsDetails[key as keyof CosellTemplatesDataFieldMappings] =
          fieldMapping
        break
      case 'Contacts':
        sectionData.contacts[key as keyof CosellTemplatesDataFieldMappings] =
          fieldMapping
        break
      case 'Deal details':
        sectionData.dealDetails[key as keyof CosellTemplatesDataFieldMappings] =
          fieldMapping
        break
      case 'Solutions offered':
        sectionData.solutionsOffered[
          key as keyof CosellTemplatesDataFieldMappings
        ] = fieldMapping
        break
      case 'Help from Microsoft':
        sectionData.helpFromMicrosoft[
          key as keyof CosellTemplatesDataFieldMappings
        ] = fieldMapping
        break
      default:
        break
    }
  })

  return sectionData
}

export const distributeReferralMappingsByCategory = (
  fieldsData: CosellReferralDataFieldMappings
): CosellReferralMappingSectionData => {
  const sectionData: CosellReferralMappingSectionData = {
    customerDetails: {},
    referralDetails: {},
    awsDetails: {},
    contacts: {},
  }

  Object.entries(fieldsData).forEach(([key, fieldMapping]) => {
    switch (fieldMapping.category) {
      case 'Customer details':
        sectionData.customerDetails[
          key as keyof CosellReferralDataFieldMappings
        ] = fieldMapping
        break
      case 'Referral details':
        sectionData.referralDetails[
          key as keyof CosellReferralDataFieldMappings
        ] = fieldMapping
        break
      case 'AWS details':
        sectionData.awsDetails[key as keyof CosellReferralDataFieldMappings] =
          fieldMapping
        break
      case 'Contacts':
        sectionData.contacts[key as keyof CosellReferralDataFieldMappings] =
          fieldMapping
        break
      default:
        break
    }
  })

  return sectionData
}

/** Maps API / legacy HubSpot-shaped field mapping entries to `CosellSyncReferralMapping`. */
export const normalizeReferralFieldMappingEntryHubspot = (
  entry: Record<string, unknown>
): CosellSyncReferralMapping => ({
  label: (entry.label as string) ?? '',
  category: (entry.category as string) ?? '',
  referralFieldType: (entry.referralFieldType as string) ?? '',
  referralField: (entry.referralField as string) ?? '',
  hubspotObject:
    (entry.hubspotObject as string) ?? (entry.crmObject as string) ?? '',
  hubspotField:
    (entry.hubspotField as string) ?? (entry.crmField as string) ?? '',
  hubspotFieldType:
    (entry.hubspotFieldType as string) ?? (entry.crmFieldType as string) ?? '',
  hubspotFieldLabel:
    (entry.hubspotFieldLabel as string) ??
    (entry.crmFieldLabel as string) ??
    '',
  syncToHubspot: Boolean(entry.syncToHubspot ?? entry.syncToCrm),
  displayOrder: (entry.displayOrder as number) ?? 0,
})

/** Maps account-scoped CRM / mapping-service entries to `CosellSyncNewReferralMapping`. */
export const normalizeReferralFieldMappingEntryCrm = (
  entry: Record<string, unknown>
): CosellSyncNewReferralMapping => ({
  label: (entry.label as string) ?? '',
  category: (entry.category as string) ?? '',
  referralFieldType: (entry.referralFieldType as string) ?? '',
  referralField: (entry.referralField as string) ?? '',
  crmObject:
    (entry.crmObject as string) ?? (entry.hubspotObject as string) ?? '',
  crmField: (entry.crmField as string) ?? (entry.hubspotField as string) ?? '',
  crmFieldType:
    (entry.crmFieldType as string) ?? (entry.hubspotFieldType as string) ?? '',
  crmFieldLabel:
    (entry.crmFieldLabel as string) ??
    (entry.hubspotFieldLabel as string) ??
    '',
  syncToCrm: Boolean(entry.syncToCrm ?? entry.syncToHubspot),
  displayOrder: (entry.displayOrder as number) ?? 0,
})

export const normalizeReferralFieldMappings = (
  fieldMappings: Record<string, unknown>,
  useCrmReferralFieldShape: boolean
): CosellReferralDataFieldMappings => {
  const normalize = useCrmReferralFieldShape
    ? normalizeReferralFieldMappingEntryCrm
    : normalizeReferralFieldMappingEntryHubspot
  const out: Record<string, CosellSyncReferralMappingEntry> = {}
  for (const [key, raw] of Object.entries(fieldMappings)) {
    if (!raw || typeof raw !== 'object') continue
    out[key] = normalize(raw as Record<string, unknown>)
  }
  return out as CosellReferralDataFieldMappings
}

export const getCurrentReferralTemplate =
  (
    crmId: string,
    accountId: string,
    templateId: string,
    useTemplateFromMappingService?: boolean,
    hasDiscared?: boolean,
    isMapped?: boolean,
    isRequired?: boolean,
    searchedInput?: string,
    /**
     * When true (cosell mapping manager multi-cloud), template reads use only
     * mapping-configuration (`getCurrentReferralTemplateSalesforce`), never crm-service
     * `referral-templates` — including before `useTemplateFromMappingService` hydrates.
     */
    isCosellMultiCloudEnabled?: boolean
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    if (isCosellMultiCloudEnabled && accountId) {
      return getCurrentReferralTemplateSalesforce(
        crmId,
        accountId,
        templateId,
        hasDiscared,
        isMapped,
        isRequired,
        searchedInput
      )(dispatch, getState)
    }
    if (isCosellMultiCloudEnabled && !accountId) {
      return
    }
    if (hasDiscared) {
      await dispatch(startLoading(LoadingTypes.DISCARD_REFERRAL_TEMPLATE))
    } else {
      await dispatch(startLoading(LoadingTypes.FETCH_CURRENT_REFERRAL_TEMPLATE))
    }
    try {
      const useTemplateFromMappingServiceOn =
        useTemplateFromMappingService ?? false
      let response: { data?: { data?: unknown } } | null = null
      if (useTemplateFromMappingServiceOn && accountId) {
        try {
          response = await getReferralTemplatesFromMappingService(
            crmId,
            templateId,
            accountId,
            isMapped,
            isRequired,
            searchedInput
          )
        } catch (mappingErr: unknown) {
          const mappingAxiosErr = mappingErr as AxiosError
          const status = (mappingAxiosErr?.response as AxiosResponse)?.status
          if (status === 404) {
            response = await getReferralTemplate(
              crmId,
              templateId,
              isMapped,
              isRequired,
              searchedInput
            )
          } else {
            throw mappingErr
          }
        }
      } else {
        response = await getReferralTemplate(
          crmId,
          templateId,
          isMapped,
          isRequired,
          searchedInput
        )
      }
      if (response && !isEmpty(response?.data)) {
        const currentReferralTemplateData = camelize(response?.data?.data)
        if (
          !currentReferralTemplateData.autoReferralTriggerCriteria &&
          currentReferralTemplateData.autoReferralDealtypeTrigger
        ) {
          currentReferralTemplateData.autoReferralTriggerCriteria = {
            object: 'Deal',
            field: 'dealtype',
            value: currentReferralTemplateData.autoReferralDealtypeTrigger,
          }
        }
        let fieldsData =
          currentReferralTemplateData?.fieldMappings as CosellTemplatesDataFieldMappings

        if (currentReferralTemplateData?.cloudProvider === 'AZURE') {
          fieldsData = {
            ...fieldsData,
            ...azureMockFieldMappings,
          }
        }

        const sectionData = distributeFieldsByCategory(fieldsData)
        await dispatch(setSectionFields(sectionData))
        await dispatch(setReferralTemplate(currentReferralTemplateData))
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      if (hasDiscared) {
        await dispatch(stopLoading(LoadingTypes.DISCARD_REFERRAL_TEMPLATE))
      } else {
        await dispatch(
          stopLoading(LoadingTypes.FETCH_CURRENT_REFERRAL_TEMPLATE)
        )
      }
    }
  }

const calculateIsMapped = (field: CosellFieldMapping): boolean => {
  const hasHubspotField = !!(
    field.hubspotField && field.hubspotField.trim() !== ''
  )
  const hasDefaultValue = !!(
    field.defaultValue &&
    (typeof field.defaultValue === 'string'
      ? field.defaultValue.trim() !== ''
      : Array.isArray(field.defaultValue) && field.defaultValue.length > 0)
  )
  return hasHubspotField || hasDefaultValue
}

export const normalizeCrmFieldMappings = (
  fieldMappings: CosellTemplatesDataFieldMappings
): CosellTemplatesDataFieldMappings => {
  if (!fieldMappings || typeof fieldMappings !== 'object') {
    return fieldMappings
  }

  return Object.fromEntries(
    Object.entries(fieldMappings).map(([key, field]) => {
      if (field?.crmField && typeof field.crmField === 'string') {
        const [hubspotObject, hubspotField] = field.crmField
          .split('.')
          .map((v: string) => v?.trim())

        if (hubspotObject && hubspotField) {
          const normalizedField = {
            ...field,
            hubspotObject,
            hubspotField,
            hubspotFieldLabel: field.crmFieldLabel,
            hubspotFieldType: field.crmFieldType,
          }
          return [
            key,
            {
              ...normalizedField,
              isMapped: calculateIsMapped(normalizedField),
            },
          ]
        }
      }

      return [
        key,
        {
          ...field,
          isMapped: calculateIsMapped(field),
        },
      ]
    })
  ) as CosellTemplatesDataFieldMappings
}

export const denormalizeCrmFieldMappings = (
  fieldMappings: CosellTemplatesDataFieldMappings
): CosellTemplatesDataFieldMappings => {
  if (!fieldMappings || typeof fieldMappings !== 'object') {
    return fieldMappings
  }

  return Object.fromEntries(
    Object.entries(fieldMappings).map(([key, field]) => {
      if (!field) return [key, field]
      const {
        hubspotObject,
        hubspotField,
        hubspotFieldLabel,
        hubspotFieldType,
        ...rest
      } = field

      const crmField =
        hubspotObject && hubspotField
          ? `${hubspotObject}.${hubspotField}`
          : rest.crmField || ''

      return [
        key,
        {
          ...rest,
          crmField,
          crmFieldLabel: hubspotFieldLabel || rest.crmFieldLabel || '',
          crmFieldType: hubspotFieldType || rest.crmFieldType || '',
        },
      ]
    })
  ) as CosellTemplatesDataFieldMappings
}

export const getCurrentReferralTemplateSalesforce =
  (
    crmId: string,
    accountId: string,
    templateId: string,
    hasDiscared?: boolean,
    isMapped?: boolean,
    isRequired?: boolean,
    searchedInput?: string
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    if (hasDiscared) {
      await dispatch(startLoading(LoadingTypes.DISCARD_REFERRAL_TEMPLATE))
    } else {
      await dispatch(startLoading(LoadingTypes.FETCH_CURRENT_REFERRAL_TEMPLATE))
    }
    try {
      const response = await getReferralTemplateForSalesforce(
        crmId,
        accountId,
        templateId,
        isMapped,
        isRequired,
        searchedInput
      )

      const rawTemplate = response?.data?.data ?? response?.data ?? response
      if (!isEmpty(rawTemplate)) {
        const rawCriteria = rawTemplate.auto_referral_trigger_criteria ?? null
        const currentReferralTemplateData = camelize(rawTemplate)
        if (
          rawCriteria &&
          typeof rawCriteria === 'object' &&
          !Array.isArray(rawCriteria)
        ) {
          currentReferralTemplateData.autoReferralTriggerCriteria = rawCriteria
        }
        if (currentReferralTemplateData.autoReferralTriggerCriteria) {
          currentReferralTemplateData.autoReferralCreation = true
        }
        let fieldsData =
          currentReferralTemplateData?.fieldMappings as CosellTemplatesDataFieldMappings

        if (currentReferralTemplateData?.cloudProvider === 'AZURE') {
          fieldsData = {
            ...fieldsData,
            ...azureMockFieldMappings,
          }
        }
        fieldsData = normalizeCrmFieldMappings(
          currentReferralTemplateData?.fieldMappings as CosellTemplatesDataFieldMappings
        )
        const sectionData = distributeFieldsByCategory(fieldsData)
        await dispatch(setSectionFields(sectionData))
        await dispatch(setReferralTemplate(currentReferralTemplateData))
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      if (hasDiscared) {
        await dispatch(stopLoading(LoadingTypes.DISCARD_REFERRAL_TEMPLATE))
      } else {
        await dispatch(
          stopLoading(LoadingTypes.FETCH_CURRENT_REFERRAL_TEMPLATE)
        )
      }
    }
  }

export const createReferralTemplate =
  (
    templateData: Record<string, string>,
    crmId: string,
    accountId: string,
    useTemplateFromMappingService?: boolean
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      await dispatch(startLoading(LoadingTypes.CREATE_REFERRAL_TEMPLATE))
      const useMappingOn = useTemplateFromMappingService ?? false
      const response =
        useMappingOn && accountId
          ? await postReferralTemplateFromMappingService(
              crmId,
              snakeize(templateData),
              accountId
            )
          : await postReferralTemplate(crmId, snakeize(templateData))
      if (!isEmpty(response?.data)) {
        const templateDataResponse = camelize(response?.data?.data)
        const templateId = templateDataResponse?.id
        await dispatch(setReferralTemplate(templateDataResponse))
        return { templateId, errorMessage: '' }
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>
      const errorMessage = getErrorMessages([RequestFailureMessage])(
        errorResponse
      )
      dispatch(
        updateAppAlert({
          message: errorMessage,
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
      return { templateId: '', errorMessage }
    } finally {
      await dispatch(stopLoading(LoadingTypes.CREATE_REFERRAL_TEMPLATE))
    }
    return {
      templateId: '',
      errorMessage: 'Failed to create template. Please try again.',
    }
  }

export const cloneReferralTemplate =
  (
    templateData: Record<string, string>,
    crmId: string,
    templateId: string,
    accountId: string,
    useTemplateFromMappingService?: boolean
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      await dispatch(startLoading(LoadingTypes.CLONE_REFERRAL_TEMPLATE))
      const useMappingOn = useTemplateFromMappingService ?? false
      const response =
        useMappingOn && accountId
          ? await postCloneReferralTemplateFromMappingService(
              crmId,
              templateId,
              snakeize(templateData),
              accountId
            )
          : await postCloneReferralTemplate(
              crmId,
              templateId,
              snakeize(templateData)
            )
      if (!isEmpty(response?.data)) {
        const templateDataResponse = camelize(response?.data?.data)
        const newTemplateId = templateDataResponse?.id
        await dispatch(setReferralTemplate(templateDataResponse))
        return { templateId: newTemplateId, errorMessage: '' }
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>
      const errorMessage = getErrorMessages([RequestFailureMessage])(
        errorResponse
      )
      dispatch(
        updateAppAlert({
          message: errorMessage,
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
      return { templateId: '', errorMessage }
    } finally {
      await dispatch(stopLoading(LoadingTypes.CLONE_REFERRAL_TEMPLATE))
    }
    return {
      templateId: '',
      errorMessage: 'Failed to clone template. Please try again.',
    }
  }

export const createReferralTemplateSalesforce =
  (crmId: string, accountId: string, templateData: Record<string, string>) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      await dispatch(
        startLoading(LoadingTypes.CREATE_REFERRAL_TEMPLATE_SALESFORCE)
      )
      const response = await postReferralTemplateSalesforce(
        crmId,
        accountId,
        snakeize(templateData) as typeof templateData
      )

      if (!isEmpty(response?.data)) {
        const responseData = response.data?.data || response.data
        const templateDataResponse = camelize(responseData)

        const templateId =
          templateDataResponse?.id ||
          responseData?.id ||
          response.data?.id ||
          response.data?.data?.id

        if (templateId) {
          await dispatch(setReferralTemplate(templateDataResponse))
          return { templateId, errorMessage: '' }
        }
      }
      return {
        templateId: '',
        errorMessage:
          'Failed to create template. Invalid response from server.',
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>
      const errorMessage = getErrorMessages([RequestFailureMessage])(
        errorResponse
      )
      dispatch(
        updateAppAlert({
          message: errorMessage,
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
      return { templateId: '', errorMessage }
    } finally {
      await dispatch(
        stopLoading(LoadingTypes.CREATE_REFERRAL_TEMPLATE_SALESFORCE)
      )
    }
  }

export const cloneReferralTemplateCrm =
  (
    crmId: string,
    accountId: string,
    templateData: Record<string, string>,
    sourceTemplateId: string
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      await dispatch(
        startLoading(LoadingTypes.CLONE_REFERRAL_TEMPLATE_SALESFORCE)
      )
      const clonePayload = {
        ...snakeize(templateData),
        template_id: sourceTemplateId,
      }
      const response = await postReferralTemplateSalesforce(
        crmId,
        accountId,
        clonePayload as Record<string, unknown>
      )

      if (!isEmpty(response?.data)) {
        const responseData = response.data?.data || response.data
        const templateDataResponse = camelize(responseData)

        const newTemplateId =
          templateDataResponse?.id ||
          responseData?.id ||
          response.data?.id ||
          response.data?.data?.id

        if (newTemplateId) {
          await dispatch(setReferralTemplate(templateDataResponse))
          return { templateId: newTemplateId, errorMessage: '' }
        }
      }
      return {
        templateId: '',
        errorMessage: 'Failed to clone template. Invalid response from server.',
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>
      const errorMessage = getErrorMessages([RequestFailureMessage])(
        errorResponse
      )
      dispatch(
        updateAppAlert({
          message: errorMessage,
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
      return { templateId: '', errorMessage }
    } finally {
      await dispatch(
        stopLoading(LoadingTypes.CLONE_REFERRAL_TEMPLATE_SALESFORCE)
      )
    }
  }

export const deleteReferralTemplate =
  (
    crmId: string,
    templateId: string,
    templateName: string,
    accountId: string,
    useTemplateFromMappingService?: boolean
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.DELETE_REFERRAL_TEMPLATE))
    try {
      const useMappingService =
        (useTemplateFromMappingService ?? false) && accountId
      if (useMappingService) {
        await deleteReferralTemplateFromMappingService(
          crmId,
          templateId,
          accountId
        )
      } else {
        await deleteSingleReferralTemplate(crmId, templateId)
      }
      await dispatch(
        updateAppAlert({
          message: `${templateName} deleted successfully.`,
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([`${templateName} deletion failed.`])(
            errorResponse
          ),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.DELETE_REFERRAL_TEMPLATE))
    }
  }

export const saveReferralTemplate =
  (
    crmId: string,
    templateId: string,
    accountId: string,
    useTemplateFromMappingService?: boolean
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.SAVE_REFERRAL_TEMPLATE))
    try {
      const templateDataResponse =
        getState()?.coSellMappingManager.templateData.templateData
      const sectionData =
        getState()?.coSellMappingManager.templateData.sectionData
      const currentUserId = getState()?.userProfile?.userProfile?.id
      const mergedAllSections = {
        ...sectionData.customerDetails,
        ...sectionData.referralDetails,
        ...sectionData.awsDetails,
        ...sectionData.contacts,
      }
      templateDataResponse.fieldMappings = mergedAllSections
      templateDataResponse.updatedBy = currentUserId

      const useMappingOn = useTemplateFromMappingService ?? false
      let payload: Record<string, unknown> = snakeize(
        templateDataResponse
      ) as Record<string, unknown>

      if (!useMappingOn) {
        delete payload.auto_referral_trigger_criteria
      } else {
        const criteria = templateDataResponse.autoReferralTriggerCriteria
        payload.auto_referral_trigger_criteria = criteria ?? null
        if (
          criteria?.object === 'Deal' &&
          criteria?.field === 'dealtype' &&
          criteria?.value
        ) {
          payload.auto_referral_dealtype_trigger = criteria.value
        }
      }

      const response =
        useMappingOn && accountId
          ? await putReferralTemplateFromMappingService(
              crmId,
              templateId,
              payload,
              accountId
            )
          : await putReferralTemplate(crmId, templateId, payload)
      if (!isEmpty(response?.data)) {
        dispatch(
          updateAppAlert({
            message: ReferralTemplateSaveSuccess,
            messageType: 'SUCCESS',
            autoClose: true,
          })
        )
        const currentReferralTemplateData = camelize(response?.data?.data)
        await dispatch(setReferralTemplate(currentReferralTemplateData))
        const fieldsData =
          currentReferralTemplateData?.fieldMappings as CosellTemplatesDataFieldMappings
        const sectionData = distributeFieldsByCategory(fieldsData)
        await dispatch(setSectionFields(sectionData))
        return true
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
      return false
    } finally {
      await dispatch(stopLoading(LoadingTypes.SAVE_REFERRAL_TEMPLATE))
    }
  }
export const saveReferralTemplateCrm =
  (
    crmId: string,
    accountId: string,
    templateId: string,
    isResetToDefault?: boolean
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.SAVE_REFERRAL_TEMPLATE))
    try {
      const templateDataResponse =
        getState()?.coSellMappingManager.templateData.templateData
      const sectionData =
        getState()?.coSellMappingManager.templateData.sectionData
      const currentUserId = getState()?.userProfile?.userProfile?.id
      const mergedAllSections = {
        ...sectionData.customerDetails,
        ...sectionData.referralDetails,
        ...sectionData.awsDetails,
        ...sectionData.contacts,
      }
      templateDataResponse.fieldMappings =
        denormalizeCrmFieldMappings(mergedAllSections)
      templateDataResponse.updatedBy = currentUserId

      if (isResetToDefault) {
        templateDataResponse.resetToDefault = true
      }

      const criteriaRaw = templateDataResponse.autoReferralTriggerCriteria
      const hasCriteria =
        criteriaRaw &&
        typeof criteriaRaw === 'object' &&
        !Array.isArray(criteriaRaw) &&
        'object' in criteriaRaw &&
        'field' in criteriaRaw &&
        'value' in criteriaRaw

      let criteriaPayload:
        | Record<string, string>
        | AutoCreateTriggerCriteria
        | null = null
      if (templateDataResponse.autoReferralCreation && criteriaRaw) {
        if (hasCriteria) {
          criteriaPayload = criteriaRaw as AutoCreateTriggerCriteria
        } else if (
          typeof criteriaRaw === 'object' &&
          !Array.isArray(criteriaRaw) &&
          !('object' in criteriaRaw)
        ) {
          criteriaPayload = criteriaRaw as Record<string, string>
        }
      }

      const { autoReferralDealtypeTrigger: _omit, ...restForSalesforce } =
        templateDataResponse
      const payload = snakeize({
        ...restForSalesforce,
      }) as Record<string, unknown>
      payload.auto_referral_trigger_criteria = criteriaPayload
      const response = await putReferralTemplateForSalesforce(
        crmId,
        templateId,
        accountId,
        payload
      )
      if (!isEmpty(response?.data)) {
        dispatch(
          updateAppAlert({
            message: isResetToDefault
              ? ReferralTemplateResetSuccess
              : ReferralTemplateSaveSuccess,
            messageType: 'SUCCESS',
            autoClose: true,
          })
        )
        if (isResetToDefault) {
          dispatch(
            getCurrentReferralTemplateSalesforce(crmId, accountId, templateId)
          )
        } else {
          const rawData = response?.data?.data ?? response?.data
          const rawCriteria =
            rawData?.auto_referral_trigger_criteria ??
            rawData?.autoReferralTriggerCriteria ??
            null
          const currentReferralTemplateData = camelize(rawData)
          if (
            rawCriteria &&
            typeof rawCriteria === 'object' &&
            !Array.isArray(rawCriteria)
          ) {
            currentReferralTemplateData.autoReferralTriggerCriteria =
              rawCriteria
          }
          await dispatch(setReferralTemplate(currentReferralTemplateData))
          const fieldsData =
            currentReferralTemplateData?.fieldMappings as CosellTemplatesDataFieldMappings
          const sectionData = distributeFieldsByCategory(fieldsData)
          await dispatch(setSectionFields(sectionData))
          return true
        }
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
      return false
    } finally {
      await dispatch(stopLoading(LoadingTypes.SAVE_REFERRAL_TEMPLATE))
    }
  }

export const deleteReferralTemplateCrm =
  (
    crmId: string,
    accountId: string,
    templateId: string,
    templateName: string
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(
      startLoading(LoadingTypes.DELETE_REFERRAL_TEMPLATE_SALESFORCE)
    )
    try {
      await deleteReferralTemplateCrmAPI(crmId, accountId, templateId)
      await dispatch(
        updateAppAlert({
          message: `${templateName} deleted successfully.`,
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )

      const state = getState()
      const rawSection = state.coSellMappingManager.sectionData
      const trimmedTemplateId = String(templateId ?? '').trim()
      if (
        trimmedTemplateId &&
        isCosellSalesforceMappingSectionData(rawSection) &&
        isSalesforceTemplateIdInRequiredFieldsSlots(
          trimmedTemplateId,
          rawSection[
            CoSellMappingManagerSectionName.UPDATE_REFERRAL_RECORD_SALESFORCE
          ]
        )
      ) {
        const patched = removeSalesforceTemplateIdFromStageMappingRefs(
          rawSection,
          trimmedTemplateId
        )
        if (patched !== rawSection) {
          const cloudType =
            String(state.coSellMappingManager.cloudType ?? '').trim() ||
            undefined
          await dispatch(
            saveCrmMappingSectionData(crmId, accountId, patched, cloudType)
          )
        }
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([`${templateName} deletion failed.`])(
            errorResponse
          ),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(
        stopLoading(LoadingTypes.DELETE_REFERRAL_TEMPLATE_SALESFORCE)
      )
    }
  }

export const updateReferralTemplateName =
  (
    crmId: string,
    templateId: string,
    templateData: Partial<CosellTemplateDataResponse>,
    accountId: string,
    useTemplateFromMappingService?: boolean
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.EDIT_REFERRAL_TEMPLATE_NAME))
    try {
      const useMappingOn = useTemplateFromMappingService ?? false
      const response =
        useMappingOn && accountId
          ? await putReferralTemplateFromMappingService(
              crmId,
              templateId,
              snakeize(templateData),
              accountId
            )
          : await putReferralTemplate(crmId, templateId, snakeize(templateData))
      if (!isEmpty(response?.data)) {
        await dispatch(
          updateAppAlert({
            message: ReferralTemplateSaveSuccess,
            messageType: 'SUCCESS',
            autoClose: true,
          })
        )
        const currentReferralTemplateData = camelize(response?.data?.data)
        await dispatch(setReferralTemplate(currentReferralTemplateData))
        const fieldsData =
          currentReferralTemplateData?.fieldMappings as CosellTemplatesDataFieldMappings
        const sectionData = distributeFieldsByCategory(fieldsData)
        await dispatch(setSectionFields(sectionData))
        return { success: true }
      }
      return { success: false }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>
      const errorMessage = getErrorMessages([RequestFailureMessage])(
        errorResponse
      )

      dispatch(
        updateAppAlert({
          message: errorMessage,
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
      return { success: false, errorMessage }
    } finally {
      await dispatch(stopLoading(LoadingTypes.EDIT_REFERRAL_TEMPLATE_NAME))
    }
  }

export const resetSingleReferralTemplate =
  (crmId: string, templateId: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.DISCARD_REFERRAL_TEMPLATE))
    try {
      const templateDataResponse =
        getState()?.coSellMappingManager.templateData.templateData
      const response = await resetReferralTemplate(
        crmId,
        templateId,
        snakeize(templateDataResponse)
      )
      if (!isEmpty(response?.data)) {
        await dispatch(
          updateAppAlert({
            message: ReferralTemplateResetSuccess,
            messageType: 'SUCCESS',
            autoClose: true,
          })
        )
        const currentReferralTemplateData = camelize(response?.data?.data)
        await dispatch(setReferralTemplate(currentReferralTemplateData))
        const fieldsData =
          currentReferralTemplateData?.fieldMappings as CosellTemplatesDataFieldMappings
        const sectionData = distributeFieldsByCategory(fieldsData)
        await dispatch(setSectionFields(sectionData))
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.DISCARD_REFERRAL_TEMPLATE))
    }
  }

const normalizeToAutoCreateTriggerCriteria = (
  raw: unknown
): AutoCreateTriggerCriteria | null => {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    const criteria = raw as { object?: string; field?: string; value?: string }
    if (criteria.object && criteria.field && criteria.value) {
      return {
        object: criteria.object,
        field: criteria.field,
        value: criteria.value,
      }
    }
    const entries = Object.entries(raw as Record<string, string>)
    if (entries.length === 1) {
      const [key, value] = entries[0]
      const parts = key.split('.')
      if (parts.length >= 2 && value) {
        return {
          object: parts[0],
          field: parts.slice(1).join('.'),
          value,
        }
      }
    }
  }
  return null
}

export const mapTemplateDataToDetails = (
  templates: any[],
  users: UserState[]
): CosellTemplatesDetailsData[] =>
  templates.map(template => {
    const user = users.find(user => user.id === template.createdBy)
    const createdByName = user?.name || '-'

    const dealtypeValue =
      template.autoReferralDealtypeTrigger ?? template.autoDealtype
    const autoReferralTriggerCriteria =
      normalizeToAutoCreateTriggerCriteria(
        template.autoReferralTriggerCriteria
      ) ??
      (dealtypeValue
        ? {
            object: 'Deal',
            field: 'dealtype',
            value: String(dealtypeValue),
          }
        : null)

    return {
      templateName: template.templateName,
      templateId: template.id,
      status: template.isDraft
        ? MappingManagerTemplateStatusType.DRAFT
        : MappingManagerTemplateStatusType.ACTIVE,
      createdBy: createdByName,
      creationDate: template.createdAt,
      lastUpdatedAt: template.updatedAt,
      autoReferralCreation: template.autoReferralCreation,
      autoReferralTriggerCriteria,
    }
  })

export const mapCrmTemplateDataToDetails = (
  templates: CosellTemplateItem[],
  users: UserState[]
): CosellTemplatesDetailsData[] =>
  templates.map(template => {
    const user = users.find(user => user.id === template.createdBy)
    const createdByName = user?.name || '-'

    const autoReferralTriggerCriteria = template.autoReferralTriggerCriteria
      ? normalizeToAutoCreateTriggerCriteria(
          template.autoReferralTriggerCriteria
        )
      : null

    return {
      templateName: template.name,
      templateId: template.id,
      status: template.isDraft
        ? MappingManagerTemplateStatusType.DRAFT
        : MappingManagerTemplateStatusType.ACTIVE,
      createdBy: createdByName,
      creationDate: template.createdAt,
      lastUpdatedAt: template.updatedAt || template.createdAt,
      autoReferralCreation: template.autoReferralCreation,
      autoReferralTriggerCriteria,
      cloudProvider: template.cloudProvider,
    }
  })

export const getReferralTemplatesInfo =
  (crmId: string, templateType?: string, isActive?: boolean) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.FETCH_REFERRAL_TEMPLATES))
    try {
      const response = await getAllReferralTemplates(
        crmId,
        isActive,
        templateType
      )
      if (!isEmpty(response?.data)) {
        const templateDetailsData = camelize(response?.data?.data?.templates)
        const usersData = getState()?.userList.user.userList.users
        const referralTemplatesData = mapTemplateDataToDetails(
          templateDetailsData,
          usersData
        )
        await dispatch(setReferralTemplatesInfo(referralTemplatesData))
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.FETCH_REFERRAL_TEMPLATES))
    }
  }

/** Fetches template list from mapping service for HubSpot when useTemplateFromMappingService flag is enabled. */
export const getReferralTemplatesInfoFromMappingService =
  (
    crmId: string,
    accountId: string,
    templateType?: string,
    productType?: string,
    isDraft?: boolean
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.FETCH_REFERRAL_TEMPLATES))
    try {
      const response = await getAllReferralTemplatesCrm(
        crmId,
        accountId,
        productType || 'cosell',
        templateType || 'referral',
        isDraft
      )
      if (!isEmpty(response?.data)) {
        type RawPayload = {
          data?: {
            mapping_templates?: Array<{
              auto_referral_trigger_criteria?: Record<string, string> | null
            }>
          }
          mappingTemplates?: unknown[]
          mapping_templates?: Array<{
            auto_referral_trigger_criteria?: Record<string, string> | null
          }>
        }
        const payload = response.data as RawPayload
        const rawData = payload?.data
        const rawTemplates: Array<{
          auto_referral_trigger_criteria?: Record<string, string> | null
        }> =
          rawData?.mapping_templates ??
          (payload as { mapping_templates?: typeof rawTemplates })
            .mapping_templates ??
          []
        const camelizedResponse = camelize(
          response.data
        ) as CosellTemplateDataResponseCRM
        const usersData = getState()?.userList.user.userList.users || []
        const mapped = mapCrmTemplateDataToDetails(
          camelizedResponse.mappingTemplates ?? [],
          usersData
        )
        const referralTemplatesData = mapped.map((detail, i) => ({
          ...detail,
          autoReferralTriggerCriteria:
            normalizeToAutoCreateTriggerCriteria(
              rawTemplates[i]?.auto_referral_trigger_criteria
            ) ??
            detail.autoReferralTriggerCriteria ??
            null,
        }))
        await dispatch(setReferralTemplatesInfo(referralTemplatesData))
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.FETCH_REFERRAL_TEMPLATES))
    }
  }

export const getReferralTemplatesInfoCRM =
  (
    crmId: string,
    accountId: string,
    templateType: string,
    productType: string,
    isDraft?: boolean
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.FETCH_REFERRAL_TEMPLATES))
    try {
      const response = await getAllReferralTemplatesCrm(
        crmId,
        accountId,
        productType,
        templateType,
        isDraft
      )
      if (!isEmpty(response?.data)) {
        type RawPayload = {
          data?: {
            mapping_templates?: Array<{
              auto_referral_trigger_criteria?: Record<string, string> | null
            }>
          }
          mappingTemplates?: unknown[]
          mapping_templates?: Array<{
            auto_referral_trigger_criteria?: Record<string, string> | null
          }>
        }
        const payload = response.data as RawPayload
        const rawData = payload?.data
        const rawTemplates: Array<{
          auto_referral_trigger_criteria?: Record<string, string> | null
        }> =
          rawData?.mapping_templates ??
          (payload as { mapping_templates?: typeof rawTemplates })
            .mapping_templates ??
          []
        const camelizedResponse = camelize(
          response.data
        ) as CosellTemplateDataResponseCRM
        const usersData = getState()?.userList.user.userList.users || []
        const mapped = mapCrmTemplateDataToDetails(
          camelizedResponse.mappingTemplates,
          usersData
        )
        const referralTemplatesData = mapped.map((detail, i) => ({
          ...detail,
          autoReferralTriggerCriteria:
            normalizeToAutoCreateTriggerCriteria(
              rawTemplates[i]?.auto_referral_trigger_criteria
            ) ??
            detail.autoReferralTriggerCriteria ??
            null,
        }))
        await dispatch(setReferralTemplatesInfo(referralTemplatesData))
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.FETCH_REFERRAL_TEMPLATES))
    }
  }

export const getAllHubspotFields =
  (crmId: string, objectType?: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.FETCH_HUBSPOT_FIELDS))
    try {
      const response = await getHubspotFieldsForTemplate(crmId, objectType)
      if (!isEmpty(response?.data)) {
        const crmFieldData = camelize(response?.data?.data?.fields)
        await dispatch(setAllHubspotFields(crmFieldData))
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.FETCH_HUBSPOT_FIELDS))
    }
  }

export const flattenSalesforceFields = (
  fieldsObj: CosellSalesforceFieldsData
) => {
  if (!fieldsObj) return []

  return Object.entries(fieldsObj).flatMap(([objectType, fields]) =>
    fields.map(
      ({
        label,
        standardValues,
        ...rest
      }: {
        label: any
        standardValues: any
        [key: string]: any
      }) => ({
        ...rest,
        displayLabel: label,
        options: standardValues,
        objectType,
      })
    )
  )
}

type LabraFieldsResponse = Record<
  string,
  Record<
    string,
    {
      label?: string
      referralField?: string
      picklist?: Array<{ value: string; label: string }>
      [key: string]: unknown
    }
  >
>

export const flattenLabraFields = (
  labraData: LabraFieldsResponse
): CosellHubspotFieldsData[] => {
  if (!labraData || typeof labraData !== 'object') return []

  const objectTypeMap: Record<string, string> = {
    opportunity: 'Opportunity',
    account: 'Account',
  }

  return Object.entries(labraData).flatMap(([sectionKey, fieldsObj]) => {
    const objectType = objectTypeMap[sectionKey] ?? sectionKey
    if (!fieldsObj || typeof fieldsObj !== 'object') return []
    return Object.entries(fieldsObj).map(
      ([fieldKey, field]: [string, Record<string, unknown>]) => {
        const label = (field.label as string) ?? fieldKey
        const referralField = (field.referralField as string) ?? fieldKey
        const picklist =
          (field.picklist as Array<{ value: string; label: string }>) ?? []
        return {
          internalName: referralField,
          displayLabel: label,
          fieldType: (field.referralFieldType as string) ?? '',
          objectType,
          description: (field.description as string) ?? '',
          options: picklist.map(opt => ({
            label: opt.label,
            value: opt.value,
          })),
        }
      }
    )
  })
}

export const getAllCrmFields =
  (crmId: string, accountId: string, crmType: CRMTypes) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.FETCH_CRM_FIELDS))
    try {
      const response = await getCrmFieldsForTemplate(crmId, accountId)
      if (!isEmpty(response?.data)) {
        const crmFieldData = camelize(response?.data?.fields)
        if (crmType === CRMTypes.salesforce) {
          const updatedFields = flattenSalesforceFields(crmFieldData)
          await dispatch(setAllHubspotFields(updatedFields))
        } else {
          await dispatch(setAllHubspotFields(crmFieldData))
        }
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.FETCH_CRM_FIELDS))
    }
  }

export const getTransformedDataFromApi = (sectionData: any) => {
  const crmAws = sectionData.crmSchema?.aws || {}
  const {
    contacts = {},
    syncFields = {},
    autoAcceptIncomingReferral = false,
    autoAcceptIncomingLead = false,
    createCompany = false,
    createContact = false,
    leadReferralPipeline = {},
    opportunityReferralPipeline = {},
    partnerCentralDetails = [],
    referralStageHubspotStageMapping = {},
    syncCustomHubspotFieldsForReporting = false,
  } = crmAws

  const enableReferralSyncToCrm = Boolean(sectionData.enableReferralSyncToCrm)

  const transformed = {
    [CoSellMappingManagerSectionName.COMPANY_AND_CONTACT]: {
      createCompany,
      createContact,
      createSenderCompany: crmAws.createSenderCompany || false,
      awsSalesRep: contacts.awsSalesRep || false,
      awsAccountManager: contacts.awsAccountManager || false,
      awsIsvSuccessManager: contacts.awsIsvSuccessManager || false,
      awsPartnerSuccessManager: contacts.awsPartnerSuccessManager || false,
      partnerDevelopmentManager: contacts.partnerDevelopmentManager || false,
      wwpsPartnerDevelopmentManager:
        contacts.wwpsPartnerDevelopmentManager || false,
      customerEndUserContactDetails:
        contacts.customerEndUserContactDetails || false,
      senderContact: contacts.senderContact || false,
    } as CoSellCompanyAndContact,

    [CoSellMappingManagerSectionName.HUBSPOT_DEAL_PIPELINE]: {
      autoAcceptIncomingReferral,
      opportunityReferralPipeline: {
        pipelineId: opportunityReferralPipeline.pipelineId,
        pipelineDealStageId: opportunityReferralPipeline.pipelineDealStageId,
      },
      autoAcceptIncomingLead,
      leadReferralPipeline: {
        pipelineId: leadReferralPipeline.pipelineId,
        pipelineDealStageId: leadReferralPipeline.pipelineDealStageId,
      },
    } as CoSellHubspotDealPipeline,

    [CoSellMappingManagerSectionName.AWS_PARTNER_USER_DETAILS]: {
      userDetails: partnerCentralDetails.map((user: any, index: number) => ({
        id: `${index}-${user.username || Math.random().toString(36).substring(2, 10)}`,
        email: user.email || '',
        firstName: user.firstName || '',
        lastName: user.lastName || '',
      })),
    } as CoSellAwsPartnerUserDetails,

    [CoSellMappingManagerSectionName.UPDATE_REFERRAL_RECORD]: {
      stage: syncFields.stage || false,
      dealOwner: syncFields.dealOwner || false,
      nextSteps: syncFields.nextSteps || false,
      targetCloseDate: syncFields.targetCloseDate || false,
      stagesMapping: {
        prospect: referralStageHubspotStageMapping?.prospect || [],
        qualified: referralStageHubspotStageMapping?.qualified || [],
        technicalValidation:
          referralStageHubspotStageMapping?.technicalValidation || [],
        businessValidation:
          referralStageHubspotStageMapping?.businessValidation || [],
        committed: referralStageHubspotStageMapping?.committed || [],
        launched: referralStageHubspotStageMapping?.launched || [],
        closedLost: referralStageHubspotStageMapping?.closedLost || [],
      },
    } as CoSellUpdateReferralRecord,

    [CoSellMappingManagerSectionName.SYNC_REFERRAL_RECORD]: {
      syncCustomHubspotFieldsForReporting,
      enableReferralSyncToCrm,
    } as CoSellSyncReferralRecord,
  }

  return transformed
}

export const getTransformedDataForApi = (
  sectionStateData: CosellMappingManagerSections,
  originalCrmSchema: any = {}
): any => {
  const sectionData = camelize(sectionStateData) as CosellMappingManagerSections
  const snakeizedCrmSchema = snakeize(originalCrmSchema)

  const companyAndContact =
    sectionData[CoSellMappingManagerSectionName.COMPANY_AND_CONTACT]
  const dealPipeline =
    sectionData[CoSellMappingManagerSectionName.HUBSPOT_DEAL_PIPELINE]
  const awsPartnerUserDetails =
    sectionData[CoSellMappingManagerSectionName.AWS_PARTNER_USER_DETAILS]
  const updateReferralRecord =
    sectionData[CoSellMappingManagerSectionName.UPDATE_REFERRAL_RECORD]
  const syncReferralRecord =
    sectionData[CoSellMappingManagerSectionName.SYNC_REFERRAL_RECORD]

  const transformedApiData = {
    aws: {
      create_company: companyAndContact.createCompany,
      create_contact: companyAndContact.createContact,
      create_sender_company: companyAndContact.createSenderCompany,
      contacts: {
        aws_sales_rep: companyAndContact.awsSalesRep,
        aws_account_manager: companyAndContact.awsAccountManager,
        aws_isv_success_manager: companyAndContact.awsIsvSuccessManager,
        aws_partner_success_manager: companyAndContact.awsPartnerSuccessManager,
        partner_development_manager:
          companyAndContact.partnerDevelopmentManager,
        wwps_partner_development_manager:
          companyAndContact.wwpsPartnerDevelopmentManager,
        customer_end_user_contact_details:
          companyAndContact.customerEndUserContactDetails,
        sender_contact: companyAndContact.senderContact,
      },
      auto_accept_incoming_referral: dealPipeline.autoAcceptIncomingReferral,
      auto_accept_incoming_lead: dealPipeline.autoAcceptIncomingLead,
      opportunity_referral_pipeline: {
        pipeline_id: dealPipeline.opportunityReferralPipeline?.pipelineId,
        pipeline_deal_stage_id:
          dealPipeline.opportunityReferralPipeline?.pipelineDealStageId,
      },
      lead_referral_pipeline: {
        pipeline_id: dealPipeline.leadReferralPipeline?.pipelineId,
        pipeline_deal_stage_id:
          dealPipeline.leadReferralPipeline?.pipelineDealStageId,
      },
      partner_central_details: (awsPartnerUserDetails?.userDetails ?? []).map(
        user => ({
          email: user.email,
          first_name: user.firstName,
          last_name: user.lastName,
        })
      ),
      sync_fields: {
        stage: updateReferralRecord.stage,
        deal_owner: updateReferralRecord.dealOwner,
        next_steps: updateReferralRecord.nextSteps,
        target_close_date: updateReferralRecord.targetCloseDate,
      },
      referral_stage_hubspot_stage_mapping: {
        prospect: updateReferralRecord.stagesMapping.prospect,
        qualified: updateReferralRecord.stagesMapping.qualified,
        technical_validation:
          updateReferralRecord.stagesMapping.technicalValidation,
        business_validation:
          updateReferralRecord.stagesMapping.businessValidation,
        committed: updateReferralRecord.stagesMapping.committed,
        launched: updateReferralRecord.stagesMapping.launched,
        closed_lost: updateReferralRecord.stagesMapping.closedLost,
      },
      sync_custom_hubspot_fields_for_reporting:
        syncReferralRecord.syncCustomHubspotFieldsForReporting,
    },
  }

  return {
    aws: {
      ...snakeizedCrmSchema,
      ...transformedApiData.aws,
    },
  }
}

export const getAllSectionData =
  (crmId: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(
      startLoading(LoadingTypes.FETCH_CO_SELL_MAPPING_SECTION_DATA)
    )
    try {
      // Fallback to API call if no CRM configuration data is available
      const response = await getCRMType(crmId)
      if (!isEmpty(response?.data)) {
        const sectionData = camelize(response.data)
        const transformedSectionData = getTransformedDataFromApi(sectionData)
        await dispatch(setCosellMappingSection(transformedSectionData))
        await dispatch(setCrmSchemaData(sectionData.crmSchema.aws))
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(
        stopLoading(LoadingTypes.FETCH_CO_SELL_MAPPING_SECTION_DATA)
      )
    }
  }

export const saveHubspotMappingSectionData =
  (crmId: string, sectionDataResponse: CosellMappingManagerSections) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.SAVE_CO_SELL_MAPPING_SECTION_DATA))
    try {
      const originalCrmSchema =
        getState().coSellMappingManager.originalCrmSchema ?? {}
      const transformedSectionData = getTransformedDataForApi(
        sectionDataResponse,
        originalCrmSchema
      )
      const valueToSendInApi = {
        crm_schema: transformedSectionData,
        crm_type: 'hubspot',
      }
      const response = await patchHubspotSectionData(crmId, valueToSendInApi)
      if (!isEmpty(response?.data)) {
        dispatch(
          updateAppAlert({
            message: ReferralTemplateSaveSuccess,
            messageType: 'SUCCESS',
            autoClose: true,
          })
        )
        const sectionData = camelize(response.data)
        const transformedSectionData = getTransformedDataFromApi(sectionData)
        await dispatch(setCosellMappingSection(transformedSectionData))
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(
        stopLoading(LoadingTypes.SAVE_CO_SELL_MAPPING_SECTION_DATA)
      )
    }
  }

export const saveCrmMappingSectionData =
  (
    crmId: string,
    accountId: string,
    sectionDataResponse:
      | CosellMappingManagerSectionsForSalesforce
      | CosellMappingManagerSections,
    cloudType?: string,
    crmType: CRMTypes = CRMTypes.salesforce
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.SAVE_CO_SELL_MAPPING_SECTION_DATA))
    try {
      const resolvedCrmType = crmType ?? CRMTypes.salesforce
      const currentUserId = getState()?.userProfile?.userProfile?.id || ''
      const cloudTypeValue = cloudType?.toUpperCase() || 'AWS'
      const existingId = getState().coSellMappingManager.id
      const hasExistingSyncConfiguration = !!existingId

      let syncConfig: any
      let autoCreateMappings: Record<string, string> = {}
      let crmTypeForApi: 'salesforce' | 'hubspot' = 'salesforce'
      let enableReferralSyncToCrmForApi: boolean | undefined
      let mapResponseToSectionData: (
        sectionData: any,
        rawResponse: any
      ) =>
        | CosellMappingManagerSections
        | CosellMappingManagerSectionsForSalesforce

      if (resolvedCrmType === CRMTypes.hubspot) {
        const originalCrmSchema =
          getState().coSellMappingManager.originalCrmSchema ?? {}
        const hubspotSections =
          sectionDataResponse as CosellMappingManagerSections
        const hubspotPayload = getTransformedDataForApi(
          hubspotSections,
          originalCrmSchema
        )
        syncConfig = hubspotPayload.aws
        enableReferralSyncToCrmForApi =
          hubspotSections[CoSellMappingManagerSectionName.SYNC_REFERRAL_RECORD]
            ?.enableReferralSyncToCrm ?? false
        crmTypeForApi = 'hubspot'
        mapResponseToSectionData = (sectionData: any) =>
          getTransformedDataFromApi({
            crmSchema: { aws: sectionData.syncConfiguration ?? {} },
            enableReferralSyncToCrm: sectionData.enableReferralSyncToCrm,
          })
      } else {
        const transformedSectionData = getTransformedDataForSalesforceApi(
          sectionDataResponse as CosellMappingManagerSectionsForSalesforce
        )
        syncConfig = (transformedSectionData as any).syncConfiguration
        autoCreateMappings =
          (transformedSectionData as any).autoCreateMappings ?? {}
        enableReferralSyncToCrmForApi = undefined
        mapResponseToSectionData = (sectionData: any, rawResponse: any) =>
          getTransformedDataFromSalesforceApi(sectionData, rawResponse)
      }

      const syncPayloadExtras =
        enableReferralSyncToCrmForApi !== undefined
          ? { enable_referral_sync_to_crm: enableReferralSyncToCrmForApi }
          : {}

      const response = hasExistingSyncConfiguration
        ? await putCrmSectionData(crmId, accountId, {
            crm_id: crmId,
            account_id: accountId,
            sync_configuration: syncConfig,
            auto_create_mappings: Object.keys(autoCreateMappings).length
              ? autoCreateMappings
              : null,
            cloud_type: cloudTypeValue,
            updated_by: currentUserId,
            crm_type: crmTypeForApi,
            id: existingId,
            ...syncPayloadExtras,
          } as any)
        : await postSalesforceSectionData(
            crmId,
            accountId,
            syncConfig,
            cloudTypeValue,
            currentUserId,
            autoCreateMappings,
            crmTypeForApi,
            enableReferralSyncToCrmForApi
          )
      if (!isEmpty(response?.data)) {
        dispatch(
          updateAppAlert({
            message: ReferralTemplateSaveSuccess,
            messageType: 'SUCCESS',
            autoClose: true,
          })
        )
        const sectionData = camelize(response.data)
        const transformedSectionData = mapResponseToSectionData(
          sectionData,
          response.data
        )
        await dispatch(setCosellMappingSection(transformedSectionData))
        await dispatch(setSyncConfigurations(sectionData))
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(
        stopLoading(LoadingTypes.SAVE_CO_SELL_MAPPING_SECTION_DATA)
      )
    }
  }

export const getCurrentReferralMapping =
  (
    crmId: string,
    accountId: string,
    hasDiscared?: boolean,
    isMapped?: boolean,
    searchedInput?: string,
    multiCloudSupport?: boolean
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    if (hasDiscared) {
      await dispatch(startLoading(LoadingTypes.DISCARD_REFERRAL_TEMPLATE))
    } else {
      await dispatch(startLoading(LoadingTypes.FETCH_CURRENT_REFERRAL_TEMPLATE))
    }
    try {
      const response =
        multiCloudSupport && accountId
          ? await getReferralMappingFromMappingService(
              crmId,
              accountId,
              isMapped,
              searchedInput
            )
          : await getReferralMapping(crmId, isMapped, searchedInput)
      if (!isEmpty(response?.data)) {
        const currentReferralMappingData = normalizeReferralFieldMappings(
          camelize(
            (multiCloudSupport && accountId
              ? response?.data?.referral_to_crm_field_mappings ||
                response?.data?.field_mappings
              : ((response?.data as { data?: { field_mappings?: unknown } })
                  ?.data?.field_mappings ?? response?.data?.field_mappings)) ??
              {}
          ) as Record<string, unknown>,
          Boolean(multiCloudSupport && accountId)
        )
        await dispatch(setReferralMapping(currentReferralMappingData))
        const sectionData = distributeReferralMappingsByCategory(
          currentReferralMappingData
        )
        await dispatch(setSectionReferralMapping(sectionData))
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      if (hasDiscared) {
        await dispatch(stopLoading(LoadingTypes.DISCARD_REFERRAL_TEMPLATE))
      } else {
        await dispatch(
          stopLoading(LoadingTypes.FETCH_CURRENT_REFERRAL_TEMPLATE)
        )
      }
    }
  }

export const saveReferralMapping =
  (crmId: string, accountId: string, multiCloudSupport?: boolean) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.SAVE_REFERRAL_TEMPLATE))
    try {
      const sectionData =
        getState()?.coSellMappingManager?.referralMapping?.sectionData
      if (!sectionData) return false

      const mergedAllSections = {
        ...sectionData.customerDetails,
        ...sectionData.referralDetails,
        ...sectionData.awsDetails,
        ...sectionData.contacts,
      }

      let transformedData: Record<string, unknown>
      if (multiCloudSupport && accountId) {
        const cleanedMappings: Record<string, CosellSyncNewReferralMapping> = {}
        for (const [key, entry] of Object.entries(mergedAllSections)) {
          cleanedMappings[key] = normalizeReferralFieldMappingEntryCrm(
            entry as unknown as Record<string, unknown>
          )
        }
        transformedData = { referralToCrmFieldMappings: cleanedMappings }
      } else {
        transformedData = { fieldMappings: mergedAllSections }
      }
      const payload = snakeize(transformedData) as CoSellReferralMapping

      const response =
        multiCloudSupport && accountId
          ? await patchReferralMappingFromMappingService(
              crmId,
              accountId,
              payload
            )
          : await putReferralMapping(crmId, payload)

      if (isEmpty(response?.data)) return false

      dispatch(
        updateAppAlert({
          message: ReferralTemplateSaveSuccess,
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )

      const currentReferralMappingData = normalizeReferralFieldMappings(
        camelize(
          (multiCloudSupport && accountId
            ? response?.data?.referral_to_crm_field_mappings
            : ((response?.data as { data?: { field_mappings?: unknown } })?.data
                ?.field_mappings ?? response?.data?.field_mappings)) ?? {}
        ) as Record<string, unknown>,
        Boolean(multiCloudSupport && accountId)
      )

      await dispatch(setReferralMapping(currentReferralMappingData))

      const categorizedSectionData = distributeReferralMappingsByCategory(
        currentReferralMappingData
      )
      await dispatch(setSectionReferralMapping(categorizedSectionData))
      return true
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
      return false
    } finally {
      await dispatch(stopLoading(LoadingTypes.SAVE_REFERRAL_TEMPLATE))
    }
  }

export const fetchSyncConfigurations =
  (crmId: string, accountId: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.FETCH_SYNC_CONFIGURATIONS))
    try {
      const response = await getSyncConfigurationsCrm(crmId, accountId)

      if (!isEmpty(response?.data)) {
        const rawSyncConfigResponse = response?.data
        const camelizedSyncConfigResponse = camelize(rawSyncConfigResponse)
        const crmTypeFromPayload = camelizedSyncConfigResponse.crmType
        if (crmTypeFromPayload === CRMTypes.hubspot) {
          const transformedSectionData = getTransformedDataFromApi({
            crmSchema: {
              aws: camelizedSyncConfigResponse.syncConfiguration ?? {},
            },
            enableReferralSyncToCrm:
              camelizedSyncConfigResponse.enableReferralSyncToCrm,
          })
          await dispatch(setCosellMappingSection(transformedSectionData))
          await dispatch(setSyncConfigurations(camelizedSyncConfigResponse))
          await dispatch(
            setCrmSchemaData(
              camelizedSyncConfigResponse.syncConfiguration ?? {}
            )
          )
        } else {
          const transformedSectionData = getTransformedDataFromSalesforceApi(
            camelizedSyncConfigResponse,
            rawSyncConfigResponse
          )
          await dispatch(setCosellMappingSection(transformedSectionData))
          await dispatch(setSyncConfigurations(camelizedSyncConfigResponse))
          await dispatch(
            setCrmSchemaData(
              camelizedSyncConfigResponse.syncConfiguration ?? {}
            )
          )
        }
        return camelizedSyncConfigResponse
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.FETCH_SYNC_CONFIGURATIONS))
    }
  }

export const updateReferralTemplateNameSalesforce =
  (
    crmId: string,
    accountId: string,
    templateId: string,
    templateData: Partial<CosellTemplateDataResponse>
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.EDIT_REFERRAL_TEMPLATE_NAME))
    try {
      const response = await putReferralTemplateForSalesforce(
        crmId,
        templateId,
        accountId,
        snakeize(templateData)
      )
      if (!isEmpty(response?.data)) {
        await dispatch(
          updateAppAlert({
            message: ReferralTemplateSaveSuccess,
            messageType: 'SUCCESS',
            autoClose: true,
          })
        )
        const currentReferralTemplateData = camelize(
          response?.data
        ) as CosellTemplateDataResponse
        await dispatch(setReferralTemplate(currentReferralTemplateData))
        const fieldsData =
          currentReferralTemplateData?.fieldMappings as CosellTemplatesDataFieldMappings
        const sectionData = distributeFieldsByCategory(fieldsData)
        await dispatch(setSectionFields(sectionData))
        return { success: true as const }
      }
      return { success: false }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>
      const errorMessage = getErrorMessages([RequestFailureMessage])(
        errorResponse
      )

      dispatch(
        updateAppAlert({
          message: errorMessage,
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
      return { success: false, errorMessage }
    } finally {
      await dispatch(stopLoading(LoadingTypes.EDIT_REFERRAL_TEMPLATE_NAME))
    }
  }

/**
 * Normalizes referral object values: converts arrays to semicolon-separated strings.
 * The /crm-field-values API returns multi-value fields as arrays (e.g., ["SaaS or PaaS"]),
 * but referralAPIToForm expects semicolon-separated strings (e.g., "SaaS or PaaS").
 */
const normalizeReferralObjectArrayValues = (
  referralObject: Record<string, any>
): Record<string, any> => {
  const normalized: Record<string, any> = {}
  for (const [key, value] of Object.entries(referralObject)) {
    normalized[key] = Array.isArray(value) ? value.join(';') : value
  }
  return normalized
}

/**
 * Fetches CRM field values from CRM and sets the referral data
 * API Response format: {"referral_object":{"Labra__Website__c":"006QL00000V1YRAYA3"}}
 * This action calls postCrmFieldValues API and updates referralObject/referralObjectTransformed
 */
export const fetchAndSetCrmFieldValues =
  (
    crmId: string,
    accountId: string,
    templateId: string,
    crmPrimaryKeyValue?: string,
    crmUserId?: string
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.FETCH_CRM_FIELD_VALUES))
    try {
      const response = await postCrmFieldValues(
        crmId,
        accountId,
        templateId,
        crmPrimaryKeyValue,
        crmUserId
      )

      if (!isEmpty(response?.data)) {
        const responseData = response.data
        // API returns: {"referral_object":{"Labra__Website__c":"value", ...}}
        const referralObject = responseData.referral_object || {}

        // Get existing referral metadata from state
        const existingReferralMetadata =
          getState().referralFormCreation.referral?.referralMetadata || {}
        const existingReferralObject =
          getState().referralFormCreation.referral?.referralObject || {}

        // Normalize array values to semicolon-separated strings before merging,
        // since referralAPIToForm expects string values with ';' delimiters.
        const normalizedReferralObject =
          normalizeReferralObjectArrayValues(referralObject)

        // Merge the normalized referral object with existing data
        const mergedReferralObject = {
          ...existingReferralObject,
          ...normalizedReferralObject,
        }

        // Set referral details - the reducer will automatically compute referralObjectTransformed
        await dispatch(
          setReferralDetails({
            referralMetadata: existingReferralMetadata,
            referralObject: mergedReferralObject,
          })
        )

        // Transform API response to form format and update the form
        const transformedFormData = referralAPIToForm(mergedReferralObject)
        await dispatch(setReferralCreationData(transformedFormData as any))

        return { success: true, data: mergedReferralObject }
      }

      return { success: false }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
      return { success: false, error }
    } finally {
      await dispatch(stopLoading(LoadingTypes.FETCH_CRM_FIELD_VALUES))
    }
  }

/**
 * Transforms field mappings from template response to ReferralFormField[] format
 * Used to populate cosell.oppsync.referralFormFields
 */
export const transformFieldMappingsToReferralFormFields = (
  fieldMappings: CosellTemplatesDataFieldMappings
): ReferralFormField[] => {
  if (!fieldMappings || typeof fieldMappings !== 'object') {
    return []
  }

  return Object.entries(fieldMappings).map(([key, field]) => {
    const mapping = field as CosellFieldMapping

    return {
      id: mapping.referralField || key,
      title: mapping.label || '',
      dataType: mapping.referralFieldType || 'string',
      isRequired: mapping.required || false,
      placeholder: mapping.label || '',
      info: '', // Add info if available in mapping
      maxLength: 255, // Default max length
      hideInCreation: mapping.isHidden || false,
      disableInCreation: mapping.isReadonly || false,
      disableInUpdate: mapping.isReadonly || false,
    }
  })
}

/**
 * Extracts solution offered options from field mappings
 */
export const extractSolutionOfferedOptions = (
  fieldMappings: CosellTemplatesDataFieldMappings
): SelectorOptions[] => {
  const solutionOfferedKey = 'Labra__Solution_offered__c'
  const solutionOfferedField = (
    fieldMappings as Record<string, CosellFieldMapping>
  )[solutionOfferedKey]
  if (!solutionOfferedField || !solutionOfferedField.picklist) {
    return []
  }

  return solutionOfferedField.picklist.map(
    (option: CosellHubspotFieldsOptions) => ({
      label: option.label || option.value,
      value: option.value,
    })
  )
}

/**
 * Fetches referral form fields from Salesforce template and sets them in cosell.oppsync.referralFormFields
 * This action calls getReferralTemplateForSalesforce API and transforms fieldMappings to ReferralFormField[] format
 */
export const fetchReferralFormFieldsForSalesforce =
  (
    crmId: string,
    accountId: string,
    templateId: string,
    productId: ProductType = 'oppsync'
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(
      startLoading(LoadingTypes.FETCH_SALESFORCE_REFERRAL_FORM_FIELDS)
    )
    try {
      const response = await getReferralTemplateForSalesforce(
        crmId,
        accountId,
        templateId
      )

      if (!isEmpty(response?.data)) {
        const templateData = camelize(response?.data)
        const fieldMappings =
          templateData?.fieldMappings as CosellTemplatesDataFieldMappings

        if (fieldMappings) {
          // Normalize the field mappings first
          const normalizedFieldMappings =
            normalizeCrmFieldMappings(fieldMappings)

          // Transform field mappings to ReferralFormField[] format
          const referralFormFields = transformFieldMappingsToReferralFormFields(
            normalizedFieldMappings
          )

          // Dispatch to set referral form fields in cosell.oppsync.referralFormFields
          await dispatch(
            actionTypeWrapper(
              productId,
              setReferralFormFields(referralFormFields)
            )
          )

          // Also extract and set solution offered options if available
          const solutionOptions = extractSolutionOfferedOptions(
            normalizedFieldMappings
          )
          if (solutionOptions.length > 0) {
            await dispatch(
              actionTypeWrapper(
                productId,
                setSolutionOfferedOptions(solutionOptions)
              )
            )
          }

          return { success: true, data: referralFormFields }
        }
      }

      return { success: false }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
      return { success: false, error }
    } finally {
      await dispatch(
        stopLoading(LoadingTypes.FETCH_SALESFORCE_REFERRAL_FORM_FIELDS)
      )
    }
  }

export const fetchLabraFields =
  (cloudProvider?: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.FETCH_LABRA_FIELDS))
    try {
      const response = await getLabraFields(cloudProvider)
      if (!isEmpty(response?.data)) {
        const labraData = camelize(response?.data) as LabraFieldsResponse
        const labraFields = flattenLabraFields(labraData)
        await dispatch(setLabraFields(labraFields))
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.FETCH_LABRA_FIELDS))
    }
  }
